import { useNavigate } from 'react-router';
import { SECTIONS } from 'src/data/sections.ts';
import { ADVANCE_DELAY_MS } from 'src/lib/constants.ts';
import { useCheck } from 'src/state/check-context.ts';

/**
 * Encapsulates the wizard navigation across section → subgroup → question,
 * including the inter-section transition screen. Returns a discriminated
 * `phase` so the page can stay purely presentational.
 */
export function useCheckFlow() {
  const navigate = useNavigate();
  const {
    answers,
    setAnswers,
    sectionIdx,
    setSectionIdx,
    subgroupIdx,
    setSubgroupIdx,
    questionIdx,
    setQuestionIdx,
    transitionFrom,
    setTransitionFrom,
    legendOverride,
    setLegendOverride
  } = useCheck();

  if (transitionFrom !== null) {
    return {
      phase: 'transition' as const,
      fromIdx: transitionFrom,
      onContinue: () => {
        setSectionIdx(transitionFrom + 1);
        setSubgroupIdx(0);
        setQuestionIdx(0);
        setTransitionFrom(null);
      },
      onBack: () => {
        const prev = SECTIONS[transitionFrom];
        setSubgroupIdx(prev.subgroups.length - 1);
        setQuestionIdx(prev.subgroups[prev.subgroups.length - 1].questions.length - 1);
        setSectionIdx(transitionFrom);
        setTransitionFrom(null);
      }
    };
  }

  const sec = SECTIONS[sectionIdx];
  const sub = sec.subgroups[subgroupIdx];
  const question = sub.questions[questionIdx];
  const current = answers[question.id];

  function onAnswer(value: number) {
    setAnswers((a) => ({ ...a, [question.id]: value }));
    setTimeout(() => {
      if (questionIdx < sub.questions.length - 1) {
        setQuestionIdx(questionIdx + 1);
      } else if (subgroupIdx < sec.subgroups.length - 1) {
        setSubgroupIdx(subgroupIdx + 1);
        setQuestionIdx(0);
      } else if (sectionIdx < SECTIONS.length - 1) {
        setTransitionFrom(sectionIdx);
      } else {
        navigate('/context');
      }
    }, ADVANCE_DELAY_MS);
  }

  function onBack() {
    if (questionIdx > 0) {
      setQuestionIdx(questionIdx - 1);
    } else if (subgroupIdx > 0) {
      const prevSub = sec.subgroups[subgroupIdx - 1];
      setSubgroupIdx(subgroupIdx - 1);
      setQuestionIdx(prevSub.questions.length - 1);
    } else if (sectionIdx > 0) {
      const prev = SECTIONS[sectionIdx - 1];
      setSectionIdx(sectionIdx - 1);
      setSubgroupIdx(prev.subgroups.length - 1);
      setQuestionIdx(prev.subgroups[prev.subgroups.length - 1].questions.length - 1);
    } else {
      navigate('/');
    }
  }

  return {
    phase: 'question' as const,
    answers,
    sectionIdx,
    subgroupIdx,
    sec,
    sub,
    question,
    current,
    onAnswer,
    onBack,
    // Globaler Fragen-Index 0 = allererste Frage der Session. Bewusst NICHT an
    // Abschnitts-/Subgroup-Reset gekoppelt → Section 2–6 öffnet die Legende nie auto.
    isFirstQuestion: sectionIdx === 0 && subgroupIdx === 0 && questionIdx === 0,
    legendOverride,
    setLegendOverride
  };
}
